import type {AppMarketConfig} from '@smart/configs';
import {appMarketConfig} from '@smart/configs';
import {useFeatureAppEnv, useSalesChannel} from '@smart/feature-app/react';
import {type Market} from '@smart/types';
import {getAppMarketConfig, makeCancelable} from '@smart/utils';
import type {ReactNode} from 'react';
import React, {createContext, useContext} from 'react';
import type {AppModel} from '../../../bff/app-model/create-app-model';
import {useLocale} from '../../hooks/use-locale';
import {fetchAppModel} from '../../utils/fetch-app-model';

type AppModelState = {
  loading: boolean;
  appModel?: AppModel;
  appMarketConfig?: AppMarketConfig;
  error?: Error;
};

const initialAppModelState: AppModelState = {
  loading: true,
};

type AppModelContext = AppModelState & {
  isDemoVehicle: boolean;
};

const initialAppModelContext: AppModelContext = {
  ...initialAppModelState,
  isDemoVehicle: false,
};

const AppModelContext = createContext<AppModelContext>(initialAppModelContext);

interface AppModelProviderProps {
  children: ReactNode;
  done?: () => void;
  loader?: ReactNode;
}

export const AppModelProvider = ({
  children,
  done,
  loader,
}: AppModelProviderProps) => {
  const [state, setState] = React.useState<AppModelState>(initialAppModelState);

  const cancelRef = React.useRef<() => void>();
  const {
    baseUrl,
    featureServices: {'endpoint-directory': endpointDirectory},
  } = useFeatureAppEnv();
  const {marketId, languageTag} = useLocale();

  const salesChannel = useSalesChannel();

  React.useEffect(() => {
    const {promise, cancel} = makeCancelable(
      fetchAppModel(baseUrl, {endpointDirectory, marketId, languageTag}),
    );

    promise
      .then((appModel) => {
        setState(() => ({
          loading: false,
          appModel,
          appMarketConfig: getAppMarketConfig(
            marketId.toUpperCase() as Market,
            appMarketConfig,
          ),
        }));
        done?.();
      })
      .catch((error) => {
        setState(() => ({
          loading: false,
          appModel: undefined,
          error,
        }));
        done?.();
      });
    cancelRef.current?.();

    return (cancelRef.current = cancel);
  }, [baseUrl, endpointDirectory, marketId, languageTag, done]);

  const value: AppModelContext = {
    ...state,
    isDemoVehicle: salesChannel === `DEMO`,
  };

  return (
    <AppModelContext.Provider value={value}>
      {loader && state.loading ? loader : children}
    </AppModelContext.Provider>
  );
};

export const useAppModel = () => {
  const context = useContext(AppModelContext);
  if (!context) {
    throw new Error(`useAppModel must be used within a AppModelProvider`);
  }
  return context;
};
